import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  EyeOff,
  Eye,
  PackageX,
  RefreshCw,
  Search,
  ShieldCheck,
  ShoppingBag,
} from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Loading,
  PageHeader,
  Panel,
  PanelBody,
  Segmented,
  StatusPill,
} from '../ui/index.js';
import { fetchHiddenProducts, hideProducts, unhideProducts, parkHiddenStock } from '../api.js';
import { SubBrandPills } from '../components/SubBrandPills.js';

/**
 * S29 — Produits masqués.
 *
 * Two tabs, one truth:
 *  - **Hidden**  — already `catalog_visibility=hidden` on the website. Their
 *    stock must be parked at -999, otherwise they can still surface in the
 *    Facebook Ads catalogue. The stock column shows the current value and
 *    flags the ones that need parking.
 *  - **À masquer** — tagged by the operator but still visible on the
 *    website: the queue to push.
 *
 * Every write goes through the verified WAL pipeline (batch + read-back).
 */

const HIDDEN_STOCK = -999;

type Tab = 'hidden' | 'stock-wrong' | 'to-hide' | 'stale';

export function HiddenProductsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('hidden');
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('all');
  const [excludedSubBrands, setExcludedSubBrands] = useState<string[]>([]);
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoomImage, setZoomImage] = useState<{ url: string; name: string } | null>(null);

  const listQuery = useQuery({
    queryKey: ['hidden-products', tab, search, brand, excludedSubBrands],
    queryFn: () =>
      fetchHiddenProducts({
        tab,
        ...(search.trim() !== '' ? { q: search.trim() } : {}),
        ...(brand !== 'all' ? { brand } : {}),
        ...(excludedSubBrands.length > 0 ? { excludedSubBrands: excludedSubBrands.join(',') } : {}),
      }),
    refetchInterval: 30_000,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['hidden-products'] });
  };

  const hideMutation = useMutation({
    mutationFn: (productIds: number[]) => hideProducts(productIds),
    onSuccess: (res) => {
      setError(null);
      setMessage(
        `${res.hiddenCount} produit(s) masqué(s) sur le site — ${res.verified} vérifié(s), ${res.failed} échec(s).`,
      );
      setSelected([]);
      invalidate();
    },
    onError: (err: Error) => {
      setMessage(null);
      setError(err.message);
    },
  });

  const unhideMutation = useMutation({
    mutationFn: (productIds: number[]) => unhideProducts(productIds),
    onSuccess: (res) => {
      setError(null);
      setMessage(
        `${res.unhiddenCount} produit(s) remis en ligne et passés en stock sur WooCommerce — ${res.verified} vérifié(s), ${res.failed} échec(s).`,
      );
      setSelected([]);
      invalidate();
    },
    onError: (err: Error) => {
      setMessage(null);
      setError(err.message);
    },
  });

  const stockMutation = useMutation({
    mutationFn: (productIds: number[]) => parkHiddenStock(productIds),
    onSuccess: (res) => {
      setError(null);
      setMessage(
        `Stock parqué à ${HIDDEN_STOCK} — ${res.verified} vérifié(s), ${res.failed} échec(s). Ces produits sortent du catalogue Facebook Ads.`,
      );
      setSelected([]);
      invalidate();
    },
    onError: (err: Error) => {
      setMessage(null);
      setError(err.message);
    },
  });

  const items = listQuery.data?.items ?? [];
  const counts = listQuery.data?.counts;
  const brands = listQuery.data?.brands ?? [];

  const needsStock = useMemo(
    () =>
      items.filter((item) =>
        item.liveKnown ? item.liveStockQty !== HIDDEN_STOCK : item.stockQty !== HIDDEN_STOCK,
      ),
    [items],
  );

  const toggle = (id: number): void => {
    setSelected((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id],
    );
  };

  const allSelected = items.length > 0 && selected.length === items.length;
  const busy = hideMutation.isPending || unhideMutation.isPending || stockMutation.isPending;

  return (
    <div>
      <PageHeader
        index="S29"
        title="Produits masqués"
        purpose="Les produits retirés de la boutique. Un produit masqué doit avoir un stock à -999 : c'est ce qui le sort du catalogue Facebook Ads."
        meta={[
          {
            label: 'Masqués',
            value: String(counts?.hidden ?? 0),
            onClick: () => {
              setTab('hidden');
              setSelected([]);
            },
          },
          {
            label: 'Stock à corriger',
            value: String(counts?.missingStock ?? 0),
            onClick: () => {
              setTab('stock-wrong');
              setSelected([]);
            },
          },
          {
            label: 'À masquer',
            value: String(counts?.toHide ?? 0),
            onClick: () => {
              setTab('to-hide');
              setSelected([]);
            },
          },
          {
            label: 'À resynchroniser',
            value: String(counts?.stale ?? 0),
            onClick: () => {
              setTab('stale');
              setSelected([]);
            },
          },
        ]}
        actions={
          <Button variant="outline" size="sm" onClick={() => void listQuery.refetch()} disabled={listQuery.isFetching}>
            <RefreshCw className={`h-4 w-4 ${listQuery.isFetching ? 'animate-spin' : ''}`} aria-hidden="true" />
            Actualiser
          </Button>
        }
      />

      {message !== null && (
        <div className="fixed right-5 top-20 z-50 max-w-md animate-rise" role="status">
          <Alert
            tone="good"
            title="Opération réussie"
            actions={
              <button
                type="button"
                onClick={() => setMessage(null)}
                className="cursor-pointer rounded p-1 text-xs font-semibold opacity-70 hover:opacity-100"
                aria-label="Fermer"
              >
                ✕
              </button>
            }
          >
            {message}
          </Alert>
        </div>
      )}
      {error !== null && (
        <div className="fixed right-5 top-20 z-50 max-w-md animate-rise" role="alert">
          <Alert
            tone="alarm"
            title="Échec"
            actions={
              <button
                type="button"
                onClick={() => setError(null)}
                className="cursor-pointer rounded p-1 text-xs font-semibold opacity-70 hover:opacity-100"
                aria-label="Fermer"
              >
                ✕
              </button>
            }
          >
            {error}
          </Alert>
        </div>
      )}

      <div className="space-y-4">
        {/* Inline toolbar — tabs + filters on one row, no rail (space matters here) */}
        <Panel>
          <PanelBody className="flex flex-wrap items-center gap-3">
            <Segmented<Tab>
              ariaLabel="État des produits masqués"
              value={tab}
              onChange={(next) => {
                setTab(next);
                setSelected([]);
              }}
              options={[
                { value: 'hidden', label: `Masqués (${counts?.hidden ?? 0})` },
                { value: 'stock-wrong', label: `Stock à corriger (${counts?.missingStock ?? 0})` },
                { value: 'to-hide', label: `À masquer (${counts?.toHide ?? 0})` },
                { value: 'stale', label: `À resynchroniser (${counts?.stale ?? 0})` },
              ]}
            />

            <div className="relative min-w-0 flex-1 sm:max-w-xs">
              <label className="sr-only" htmlFor="hidden-search">
                Rechercher un produit
              </label>
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-ghost"
                aria-hidden="true"
              />
              <input
                id="hidden-search"
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom ou SKU…"
                className="h-9 w-full rounded-lg border border-border bg-card pl-9 pr-3 text-sm text-ink shadow-xs placeholder:text-ink-ghost"
              />
            </div>

            {brands.length > 0 && (
              <label className="flex items-center gap-2">
                <span className="eyebrow text-ink-faint">Marque</span>
                <select
                  value={brand}
                  onChange={(e) => {
                    setBrand(e.target.value);
                    setExcludedSubBrands([]);
                  }}
                  className="h-9 rounded-lg border border-border bg-card px-2.5 text-sm font-medium text-ink shadow-xs"
                >
                  <option value="all">Toutes</option>
                  {brands.map((b) => (
                    <option key={b.brand} value={b.brand}>
                      {b.brand} ({b.count})
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="w-full">
              <SubBrandPills
                brand={brand}
                excludedSubBrands={excludedSubBrands}
                onChange={setExcludedSubBrands}
              />
            </div>
          </PanelBody>
        </Panel>

        {tab !== 'stock-wrong' && (counts?.missingStock ?? 0) > 0 && (
          <Alert
            tone="warn"
            title="Stock à corriger"
            actions={
              <Button
                variant="outline"
                size="xs"
                onClick={() => {
                  setTab('stock-wrong');
                  setSelected([]);
                }}
              >
                Voir les {counts?.missingStock} produit(s)
              </Button>
            }
          >
            {counts?.missingStock} produit(s) masqués n&apos;ont pas un stock à {HIDDEN_STOCK} sur le
            site : ils peuvent encore apparaître dans le catalogue Facebook Ads.
          </Alert>
        )}

        {tab === 'stock-wrong' && (
          <Alert
            tone="warn"
            title={`Stock à corriger (${items.length} produit${items.length > 1 ? 's' : ''})`}
            actions={
              items.length > 0 ? (
                <Button
                  variant="primary"
                  size="xs"
                  disabled={busy}
                  onClick={() => stockMutation.mutate(items.map((i) => i.id))}
                >
                  <PackageX className="h-3.5 w-3.5" aria-hidden="true" />
                  Tout corriger ({items.length})
                </Button>
              ) : undefined
            }
          >
            Ces produits sont masqués sur la boutique mais leur stock sur WooCommerce n&apos;est pas
            parqué à {HIDDEN_STOCK}. Tant que leur stock reste positif ou à 0, ils peuvent continuer
            à alimenter les catalogues publicitaires (Facebook Ads, Google Shopping). Cliquez sur
            « Tout corriger » ou cochez les produits voulus puis cliquez sur « Park le stock à -999 ».
          </Alert>
        )}

        {tab === 'stale' && (
          <Alert tone="alarm" title="État désynchronisé — corrigé">
            Ces produits étaient enregistrés comme masqués dans notre base, mais le site les montre
            toujours (démasqués en boutique, ou masquage resté bloqué). L&apos;état affiché vient du
            site et notre base a été resynchronisée : ils ne sont plus comptés comme masqués.
          </Alert>
        )}

        {listQuery.data !== undefined && !listQuery.data.liveChecked && (
          <Alert tone="warn" title="Vérification du site indisponible">
            WooCommerce n&apos;a pas répondu : l&apos;écran affiche notre base interne, qui peut être
            en retard sur le site. Vérifiez directement sur la boutique.
          </Alert>
        )}

        {tab === 'hidden' && (counts?.unverified ?? 0) > 0 && (
          <Alert tone="warn" title="État non vérifié">
            {counts?.unverified} produit(s) n&apos;ont pas pu être vérifiés sur le site (WooCommerce
            injoignable) : leur état affiché vient de notre base, pas de la boutique.
          </Alert>
        )}

          <Panel>
            <PanelBody className="space-y-3">
              {/* Bulk bar */}
              <div className="flex flex-wrap items-center gap-2 border-b border-border pb-3">
                <label className="flex items-center gap-2 text-xs font-semibold text-ink-soft">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => setSelected(e.target.checked ? items.map((i) => i.id) : [])}
                    className="h-4 w-4 rounded border-border-strong accent-herb-600"
                  />
                  Tout sélectionner
                </label>
                <span className="text-xs font-semibold text-ink-soft tabular">
                  {selected.length} sélectionné{selected.length > 1 ? 's' : ''}
                </span>
                <span className="flex-1" />
                {tab === 'to-hide' ? (
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={busy || selected.length === 0}
                    onClick={() => hideMutation.mutate([...selected])}
                  >
                    <EyeOff className="h-4 w-4" aria-hidden="true" />
                    Masquer maintenant
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={busy || (selected.length === 0 && tab !== 'stock-wrong')}
                      onClick={() =>
                        stockMutation.mutate(
                          selected.length > 0 ? [...selected] : items.map((i) => i.id),
                        )
                      }
                      title="Passe le stock à -999 : le produit sort du catalogue Facebook Ads"
                    >
                      <PackageX className="h-4 w-4" aria-hidden="true" />
                      {tab === 'stock-wrong' && selected.length === 0
                        ? `Tout parker à -999 (${items.length})`
                        : 'Park le stock à -999'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={busy || selected.length === 0}
                      onClick={() => unhideMutation.mutate([...selected])}
                      className="border-emerald-300 text-emerald-800 hover:bg-emerald-50 hover:border-emerald-500"
                    >
                      <Eye className="h-4 w-4 text-emerald-600" aria-hidden="true" />
                      Remettre en ligne & en stock{selected.length > 0 ? ` (${selected.length})` : ''}
                    </Button>
                  </>
                )}
              </div>

              {listQuery.isLoading && <Loading label="Lecture des produits masqués…" />}
              {listQuery.isError && (
                <Alert tone="alarm" title="Liste indisponible">
                  {String(listQuery.error.message)}
                </Alert>
              )}

              {listQuery.data !== undefined && items.length === 0 && (
                <EmptyState
                  icon={<ShieldCheck className="h-6 w-6" aria-hidden="true" />}
                  title={
                    tab === 'hidden'
                      ? 'Aucun produit masqué'
                      : tab === 'stock-wrong'
                        ? 'Aucun stock à corriger'
                        : tab === 'to-hide'
                          ? 'Aucun produit en attente de masquage'
                          : 'Aucun produit à resynchroniser'
                  }
                  hint={
                    tab === 'stock-wrong'
                      ? 'Tous les produits masqués ont bien leur stock parqué à -999 sur la boutique.'
                      : tab === 'hidden'
                        ? 'Les produits retirés de la boutique apparaîtront ici, avec leur stock.'
                        : tab === 'to-hide'
                          ? 'Marquez un produit « à masquer » depuis le Workbench : il rejoindra cette file.'
                          : 'Les produits dont l’état différait entre notre base et WooCommerce ont été réalignés.'
                  }
                />
              )}

              {items.length > 0 && (
                <ul className="divide-y divide-border overflow-hidden rounded-xl border border-border">
                  {items.map((item) => {
                    const isSelected = selected.includes(item.id);
                    // The LIVE stock is the truth (the mirror can be stale);
                    // the mirrored value is only a fallback.
                    const liveStock = item.liveKnown
                      ? (item.liveStockQty ?? '∅')
                      : (item.stockQty ?? '∅');
                    const stockOk = item.liveKnown
                      ? item.liveStockQty === HIDDEN_STOCK
                      : item.stockQty === HIDDEN_STOCK;
                    return (
                      <li
                        key={item.id}
                        className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 text-sm ${
                          isSelected ? 'bg-herb-50/50' : 'bg-card'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggle(item.id)}
                          aria-label={`Sélectionner ${item.name}`}
                          className="h-4 w-4 rounded border-border-strong accent-herb-600"
                        />

                        {item.image !== null ? (
                          <button
                            type="button"
                            onClick={() => {
                              if (item.image !== null) {
                                setZoomImage({ url: item.image, name: item.name });
                              }
                            }}
                            className="shrink-0 cursor-zoom-in rounded-xl border border-border bg-white p-1 shadow-xs transition hover:border-herb-600/40 hover:shadow-card"
                            title="Agrandir la photo"
                          >
                            <img
                              src={item.image}
                              alt={`Photo de ${item.name}`}
                              width={96}
                              height={96}
                              loading="lazy"
                              className="h-24 w-24 rounded-lg object-contain"
                            />
                          </button>
                        ) : (
                          <span
                            className="grid h-24 w-24 shrink-0 place-items-center rounded-xl border border-dashed border-border bg-cream text-ink-ghost"
                            aria-hidden="true"
                          >
                            <ShoppingBag className="h-6 w-6" />
                          </span>
                        )}

                        <div className="min-w-0 flex-1">
                          <p className="truncate font-semibold text-ink" title={item.name}>
                            {item.name}
                          </p>
                          <p className="font-mono text-[11px] text-ink-faint">
                            {item.sku}
                            {item.brand !== null ? ` · ${item.brand}` : ''}
                            {item.gtin !== null ? ` · ${item.gtin}` : ''}
                          </p>
                          <div className="flex flex-wrap items-center gap-1.5 mt-1">
                            {item.hiddenBy && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200"
                                title={item.hiddenAt ? `Masqué le ${new Date(item.hiddenAt).toLocaleString('fr-FR')}` : 'Origine du masquage'}
                              >
                                <EyeOff className="h-2.5 w-2.5 text-slate-500" />
                                <span>Masqué par : <strong className="text-slate-900">{item.hiddenBy}</strong></span>
                                {item.hiddenAt && (
                                  <span className="text-[9px] text-slate-500">
                                    ({new Date(item.hiddenAt).toLocaleDateString('fr-FR')})
                                  </span>
                                )}
                              </span>
                            )}
                            {item.stockParkedBy && (
                              <span
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-50 text-emerald-800 border border-emerald-200"
                                title={item.stockParkedAt ? `Stock à -999 appliqué le ${new Date(item.stockParkedAt).toLocaleString('fr-FR')}` : 'Stock à -999'}
                              >
                                <span>Stock -999 : <strong>{item.stockParkedBy}</strong></span>
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Stock — LIVE value (the mirror can be stale) */}
                        {(tab === 'hidden' || tab === 'stock-wrong') && (
                          <StatusPill
                            tone={stockOk ? 'ok' : 'warn'}
                            title={
                              stockOk
                                ? 'Stock parqué sur le site : le produit est absent du catalogue Facebook Ads'
                                : `Stock réel ${liveStock} — le produit peut encore apparaître dans le catalogue Facebook Ads`
                            }
                          >
                            {stockOk ? `stock ${liveStock}` : `stock ${liveStock} — à corriger`}
                          </StatusPill>
                        )}

                        {tab === 'to-hide' && (
                          <StatusPill tone="warn" title="Tagué pour masquage, encore visible sur le site">
                            encore visible
                          </StatusPill>
                        )}

                        {tab === 'stale' && (
                          <StatusPill
                            tone="ko"
                            title="Notre base disait « masqué » mais le site le montre : l’état a été resynchronisé"
                          >
                            site visible
                          </StatusPill>
                        )}

                        {(tab === 'hidden' || tab === 'stock-wrong') && item.liveKnown && (
                          <Badge tone="muted">
                            {item.liveCatalogVisibility ?? 'visible'} · {item.liveStatus ?? '—'}
                          </Badge>
                        )}

                        {(tab === 'hidden' || tab === 'stock-wrong') && (
                          <div className="flex items-center ml-auto">
                            <Button
                              variant="outline"
                              size="xs"
                              disabled={busy}
                              title="Remettre ce produit visible et repasser en stock (instock) sur WooCommerce"
                              onClick={() => unhideMutation.mutate([item.id])}
                              className="font-medium text-xs border-emerald-300 text-emerald-800 hover:bg-emerald-50 hover:border-emerald-500 shadow-xs"
                            >
                              <Eye className="h-3.5 w-3.5 text-emerald-600 mr-1" />
                              {unhideMutation.isPending && unhideMutation.variables?.includes(item.id)
                                ? 'Envoi...'
                                : 'Remettre en ligne & en stock'}
                            </Button>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </PanelBody>
          </Panel>

      {(tab === 'hidden' || tab === 'stock-wrong') && needsStock.length > 0 && (
        <p className="flex items-start gap-2 text-xs text-ink-soft">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-clay-600" aria-hidden="true" />
          <span>
            Un stock négatif (-999) empêche la publication dans les catalogues publicitaires
            (Facebook Ads, Google Shopping) : le produit reste invisible même si le flux
            exporte le catalogue. {needsStock.length} produit(s) masqué(s) sont dans ce cas.
          </span>
        </p>
      )}

      {/* Photo lightbox — click a packshot to review it visually */}
      {zoomImage !== null &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[1001] flex items-center justify-center bg-black/80 p-4 sm:p-6 backdrop-blur-xs overflow-y-auto animate-fade"
            onClick={() => setZoomImage(null)}
            role="dialog"
            aria-modal="true"
            aria-label={`Photo de ${zoomImage.name}`}
          >
            <div className="max-h-[90vh] max-w-3xl my-auto animate-rise" onClick={(e) => e.stopPropagation()}>
              <img
                src={zoomImage.url}
                alt={`Photo de ${zoomImage.name}`}
                className="max-h-[80vh] w-auto rounded-2xl border border-border bg-white object-contain shadow-2xl"
              />
              <p className="mt-3 text-center text-sm font-semibold text-white">{zoomImage.name}</p>
            </div>
          </div>,
          document.body,
        )}
      </div>
    </div>
  );
}
